const WhyChooseUs = () => {
  const benefits = [
    {
      icon: 'ri-ruler-2-line',
      title: 'Precision Measurements',
      description: 'Over 30 precise measurements ensure a perfect fit that surpasses any off-the-rack garment.',
    },
    {
      icon: 'ri-palette-line',
      title: 'Thousands of Fabrics',
      description: '5000+ fabrics available for suits from world-renowned mills in Europe.',
    },
    {
      icon: 'ri-hand-heart-line',
      title: 'Expert Craftsmanship',
      description: '55+ years of tailoring experience with Bespoke Tailors (100% Custom Made) construction techniques.',
    },
    {
      icon: 'ri-home-office-line',
      title: 'House and Office Calls',
      description: 'We make house and office calls throughout Massachusetts and New England.',
    },
    {
      icon: 'ri-time-line',
      title: 'Fast Turnaround',
      description: 'Quality craftsmanship delivered in 6–8 week turnaround with rush services available.',
    },
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Why Choose Custom Clothiers?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Experience the difference of true bespoke tailoring with unmatched quality and service
          </p>
        </div>

        {/* Benefits Grid - Row 1: 3 cards, Row 2: 2 cards centered */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          
          {/* Card 1: Large Background Number Style */}
          <div className="group p-8 rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-100 hover:border-[#C4A962] hover:shadow-xl transition-all duration-300 relative overflow-hidden">
            <div className="absolute top-0 right-0 text-[120px] font-bold text-[#C4A962]/5 leading-none -mr-4 -mt-4">01</div>
            <div className="relative">
              <div className="w-14 h-14 bg-[#C4A962] rounded-xl flex items-center justify-center mb-6 transform group-hover:scale-110 transition-transform">
                <i className={`${benefits[0].icon} text-2xl text-white`}></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{benefits[0].title}</h3>
              <p className="text-gray-600 leading-relaxed">{benefits[0].description}</p>
            </div>
          </div>

          {/* Card 2: Left Border Accent with Horizontal Layout */}
          <div className="group p-8 rounded-2xl bg-white border-l-4 border-[#C4A962] shadow-lg hover:shadow-2xl transition-all duration-300">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-[#C4A962] to-[#B39952] rounded-2xl flex items-center justify-center flex-shrink-0 transform group-hover:rotate-6 transition-transform">
                <i className={`${benefits[1].icon} text-3xl text-white`}></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefits[1].title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefits[1].description}</p>
              </div>
            </div>
          </div>

          {/* Card 3: Dark Background with Gold Accent */}
          <div className="group p-8 rounded-2xl bg-gray-900 hover:bg-gray-800 transition-all duration-300 shadow-xl relative overflow-hidden">
            <div className="absolute bottom-0 right-0 w-32 h-32 bg-[#C4A962]/10 rounded-full -mr-16 -mb-16"></div>
            <div className="relative">
              <div className="w-16 h-16 bg-[#C4A962]/20 rounded-full flex items-center justify-center mb-6">
                <i className={`${benefits[2].icon} text-3xl text-[#C4A962]`}></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{benefits[2].title}</h3>
              <p className="text-gray-300 leading-relaxed">{benefits[2].description}</p>
            </div>
          </div>

          {/* Cards 4 & 5 — span full 3-col row, displayed as 2 centered cards */}
          <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-8 lg:px-24">

            {/* Card 4: Icon with Styled Background */}
            <div className="group rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-50 to-yellow-50 opacity-60"></div>
              <div className="relative p-8 bg-gradient-to-br from-white/95 to-white/90">
                <div className="w-14 h-14 bg-[#C4A962] rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <i className={`${benefits[3].icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefits[3].title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefits[3].description}</p>
              </div>
            </div>

            {/* Card 5: Gradient Border with Floating Icon */}
            <div className="group p-8 rounded-2xl bg-white relative overflow-hidden hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#C4A962] via-[#B39952] to-[#C4A962] p-[2px]">
                <div className="w-full h-full bg-white rounded-2xl"></div>
              </div>
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-[#C4A962]/10 to-[#B39952]/10 rounded-3xl flex items-center justify-center mb-6 transform group-hover:-translate-y-2 transition-transform">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#C4A962] to-[#B39952] rounded-2xl flex items-center justify-center">
                    <i className={`${benefits[4].icon} text-3xl text-white`}></i>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefits[4].title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefits[4].description}</p>
              </div>
            </div>

          </div>

        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;